import styled from '@emotion/styled';
import {colors} from '@styles/colors';
import {absoluteCenter} from '@styles/reusable';
import {useEffect, useState} from 'react';
import {StyledImage} from './StudentThumbnail.styled';
import PlaceholderPerson from '@components/PlaceholderPerson';
import {useStore} from '@store/appStore';
import { shallow } from 'zustand/shallow';
import { mq } from '@styles/helpers';
import { Devices } from '@styles/tokens';
import { useAuthentication } from '@providers/AuthProvider';

interface IProps {
  src: string,
  name: string, 
  highRes?:boolean, 
  top?:string, 
  font?:string, 
  bigFont?:string,
  splitDisplayName?:boolean,
  graph?:boolean,
  regView?:boolean,
  demo?:string,
  profile?:boolean,
}

export const StudentThumbnail = (props:IProps) => {
  const { systemSettings } = useStore();
  const { getProfilePic } = useAuthentication();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [graphImage, setGraphImage] = useState("");
  const [letters, setLetters] = useState({initials:"", color:""});
  let profilePicType = systemSettings?.find(s => s.APP_Setting_ID === 101)
  // console.log(profilePicType.value)

  useEffect(() => {
    setLetters(GetInitials(props?.name));
    if(props.graph){
      GetGraphImage()
    }
  }, [props.src])

  async function GetGraphImage () {
    let t = await getProfilePic("240x240").catch(() => null); // no token / Graph down: initials instead
    setGraphImage(t);
  }

  const { settings } = useStore(
    (state) => ({
      settings: state.settings,
    }),
    shallow,
  );

  function GetInitials(n){
    const colors = [
     "#0066CC", // Bright Blue
  "#0099FF", // Sky Blue
  "#3366FF", // Royal Blue
  "#0033CC", // Bold Blue
  "#6600CC", // Purple
  "#9933FF", // Light Purple
  "#9900CC", // Magenta
  "#CC33FF", // Light Violet
  "#6633FF", // Indigo
  "#3300FF", // Electric Indigo
  "#006699", // Teal Blue
  "#3399FF", // Bright Sky
  "#003366", // Deep Blue
  "#3333FF", // Vivid Blue
  "#6666FF", // Soft Blue
  "#660066", // Strong Purple
  "#9900FF", // Neon Purple
  "#8000FF", // Bright Violet
  "#4B0082", // Indigo
  "#5D00FF", // Royal Violet
  "#1E90FF", // Dodger Blue
  "#007FFF", // Azure
  "#6A5ACD", // Slate Blue
  "#7B68EE", // Medium Slate Blue
  "#4169E1", // Royal Blue
  "#0000CD", // Medium Blue
  "#8A2BE2", // Blue Violet
  "#9370DB", // Medium Purple
  "#BA55D3", // Orchid
  "#DA70D6"  // Orchid Light
    ];

    let hash = 0;
    for (let i = 0; i < n?.length; i++) {
      hash = n.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = colors[Math.abs(hash) % colors.length];

    const parts = n?.trim().split(/\s+/);
    let initials = "";
    if(parts){
      
      initials = parts[0][0] || '';
      if (parts.length > 1) {
        initials += parts[1][0];
      } else if (parts[0].length > 1) {
        initials += parts[0][1];
      }
      initials = initials.toUpperCase();
    }
      
    return { initials, color };
  }
  

  return (
    <>
      {profilePicType.value === "Only initials"?
      <Placeholder profile={props.profile} top={props.top} regView={props.regView} color={colors.mainTextColor}>
        <Letters bigFont={props.bigFont} top={props.regView?"0px":props.top} font={props.font} bgColor={letters.color} txtColor={""}><span>{props.splitDisplayName?letters.initials.split("-")[1]:letters.initials}</span></Letters>
      </Placeholder>
      :
        <>
        <StyledImage
        
        src={props.demo !== null && props.demo !== undefined?`../images/demo/${props.demo}.jpg`:props.graph === true?graphImage:`${props?.highRes? settings.USER_IMAGE_URL:settings.IMAGE_URL}/${props?.src}.jpg${settings.IMAGE_SAS_TOKEN}`}
        onError={(e) => {
          setImageLoaded(false);
        }}
        onLoad={() => {
          setImageLoaded(true);
        }}
        />
      
      {imageLoaded === false ? (
        <Placeholder profile={props.profile} top={props.top} regView={props.regView} color={colors.mainTextColor}>
        {profilePicType.value === "Pictures and initials"? 
          <Letters bigFont={props.bigFont} top={props.regView?"0px":props.top} font={props.font} bgColor={letters.color} txtColor={""}><span>{props.splitDisplayName?letters.initials.split("-")[1]:letters.initials}</span></Letters>
          :
          <PlaceholderPerson />
        }
        </Placeholder>
        ) : null}
      </>
    }
    </>
  );
};

const Placeholder = styled.div<{ color: string, regView?:boolean, top?:string, profile?:boolean }>`
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ color }) => (color === colors.mainTextColor ? colors.white : colors.mainTextColor)};
  overflow: hidden;
  padding: ${props => props.regView?"10px":"0"};
  display: ${props => props.regView?"flex":"block"};
  align-items: ${props => props.regView?"center":"unset"};
  justify-content: ${props => props.regView?"center":"unset"};

  > div{
    
    border-radius: ${props => props.regView?"250px":"0"};
    height: ${props => props.regView?(props.top?"60%":"90%"):"100%"};
    width: ${props => props.regView?"auto":"100%"};
    aspect-ratio: ${props => props.regView?"1 / 1":"unset"};
    position: relative;
    top:${props => props.top?props.top:"0"};
  }

  svg {
    ${absoluteCenter}
    position: absolute;
    height: 100%;
    width: auto;
  }

  ${mq(Devices.mobile)}{
    > div{

      height: ${props => props.profile?"90%":(props.regView?(props.top?"60%":"90%"):"100%")} !important;
      top:${props => props.profile?"0":(props.top?props.top:"0")};
    }
  }
`;

const Letters = styled.div<{ bgColor: string, txtColor:string, top:string, font:string, bigFont:string}>`
  background:${props => props.bgColor};
  width:100%;
  height:100%;
  color:#fff;
  font-weight: 600;
  display:flex;
  align-items: center;
  justify-content: center;

  span{
    position: relative;
    display:inline-block;
    font-size: ${props => props.font?(props.bigFont?props.bigFont:props.font) :"3.5em"};
    top: ${props => props.top?props.top :"0"};
  }

  ${mq(Devices.mobile)}{
    span{
      font-size: ${props => props.bigFont?props.font:"2.2em"} !important;

    }
  }
`;

export default StudentThumbnail;
